import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import GTop100Logo from '../assets/GTop100Logo';
import api from '../utils/api';
import './Login.css';

const NX_REWARD_PER_VOTE = 2000;

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [voteStatus, setVoteStatus] = useState(null);
  const [voteStatusMessage, setVoteStatusMessage] = useState('');
  const [checkingVote, setCheckingVote] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    
    if (token && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const { username, password } = formData;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    }
    
    return newErrors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length === 0) {
      setIsLoading(true);
      
      try {
        // Submit to backend API
        const res = await api.post('/auth/login', {
          username,
          password
        });
        
        // Store token and user info
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('username', res.data.user.username || username);
        
        setLoginSuccess(true);
        
        // Redirect to dashboard after successful login
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } catch (err) {
        // Handle errors from API
        if (err.response && err.response.data) {
          // Check if account is banned
          if (err.response.status === 403) {
            setErrors({ 
              server: `Account banned: ${err.response.data.reason || 'Violation of server rules'}`
            });
          } else {
            setErrors({ 
              server: err.response.data.msg || 'Invalid credentials'
            });
          }
        } else {
          setErrors({ 
            server: 'Server error, please try again' 
          });
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const handleVote = () => {
    const username = document.getElementById('vote-username').value;
    if (!username.trim()) {
      setVoteStatus('error');
      setVoteStatusMessage('Please enter your username');
      return;
    }
    
    // Store the username in localStorage to check status later
    localStorage.setItem('voteAttemptUsername', username);
    localStorage.setItem('voteAttemptTime', new Date().getTime());
    
    setVoteStatus('pending');
    setVoteStatusMessage('Vote page opened. Complete the vote on the new tab...');
    
    // Open GTop100 in a new tab
    window.open(`https://gtop100.com/MapleStory/Kuro-a-revolutionary-MapleStory-private-server-entering-its-Alpha-stage-soon-104650?vote=1&pingUsername=${username}`, '_blank');
    
    // Start checking for vote status
    setCheckingVote(true);
    checkVoteStatus(username);
  };

  const checkVoteStatus = (username) => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const checkInterval = setInterval(async () => {
      attempts++;
      try {
        // Check vote status for non-logged-in users
        const res = await api.get(`/voting/check-status?username=${username}`);
        
        if (res.data.recentlyVoted) {
          clearInterval(checkInterval);
          setVoteStatus('success');
          setVoteStatusMessage(`Vote successful! ${NX_REWARD_PER_VOTE} NX has been added to your account.`);
          setCheckingVote(false);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          setVoteStatus('warning');
          setVoteStatusMessage('Unable to confirm vote. If you completed the vote, rewards may be added to your account later.');
          setCheckingVote(false);
        }
      } catch (err) {
        console.error('Error checking vote status:', err);
        if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          setCheckingVote(false);
          
          if (err.response && err.response.data && err.response.data.msg) {
            setVoteStatus('error');
            setVoteStatusMessage(err.response.data.msg);
          } else {
            setVoteStatus('error');
            setVoteStatusMessage('Error checking vote status. Please try logging in to check your rewards.');
          }
        }
      }
    }, 5000); // Check every 5 seconds
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Login</h1>
        
        {loginSuccess && (
          <div className="success-message">
            Login successful! Redirecting...
          </div>
        )}
        
        <form onSubmit={onSubmit} className="login-form">
          {errors.server && (
            <div className="error-message">
              {errors.server}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={username}
              onChange={onChange}
              className={errors.username ? 'error' : ''}
              disabled={isLoading || loginSuccess}
            />
            {errors.username && <span className="error-text">{errors.username}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={onChange}
              className={errors.password ? 'error' : ''}
              disabled={isLoading || loginSuccess}
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>
          
          <button 
            type="submit" 
            className={`login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || loginSuccess}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
          
          <div className="login-footer">
            <p>Don't have an account? <Link to="/register">Register</Link></p>
          </div>
        </form>
      </div>
      
      <div className="gtop100-container">
        <h3>Vote for Us!</h3>
        <div className="vote-section">
          <GTop100Logo />
          <p className="vote-info">Vote up to 3 times a day to receive 2000 NX!</p>
          
          {voteStatus && (
            <div className={`vote-message ${voteStatus}`}>
              {voteStatusMessage}
            </div>
          )}
          
          {checkingVote && (
            <div className="vote-checking">
              <div className="spinner"></div>
              <p>Checking vote status...</p>
            </div>
          )}
          
          <div className="vote-form">
            <input 
              type="text" 
              placeholder="Enter your username" 
              id="vote-username"
              disabled={checkingVote}
            />
            <button 
              onClick={handleVote} 
              className="vote-button"
              id="vote-now-btn"
              disabled={checkingVote}
            >
              {checkingVote ? 'Processing...' : 'Vote Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;